import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Modal,
  TextField,
  Typography
} from '@mui/material';

import LoadingButton from '../../../../../components/buttons/Button';
import useSearchableSelect from '../../../../../components/hooks/useSearchableSelect';

import {
  CreateUsitcSpiRateBIZcoreTD,
  UpdateUsitcSpiRateBIZcoreTD,
  GetUsitcHtsCodesDropdownBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import {
  CLEAR_USITC_HTS_CODES_DROPDOWN_BIZ_CORE_TD
} from '../../../../../redux/actionTypes';

import global from "../../../../../global.scss";

const MODES = { CREATE: 1, EDIT: 2 };

const Data = {
  HtsCode: null,
  SpiCode: "",
  PreferentialRate: "",
};

const UsitcSpiRatesModal = ({
  open,
  setOpen,
  title,
  update,
  handleGet
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [mode, setMode] = useState(MODES.CREATE);

  const {
    results: htsResults,
    loading: htsSearchLoading,
    handleSearch: handleHtsSearch,
    reset: resetHtsSearch,
  } = useSearchableSelect(
    GetUsitcHtsCodesDropdownBIZcoreTD,
    (state) => {
      const list = state["business.core.tradeDefence"].usitcHtsCodesDropdownList;
      if (list?.records && Array.isArray(list.records)) {
        return list.records;
      }
      return [];
    },
    null,
    { limit: 10, formatQuery: (q) => `&search=${q}` }
  );

  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    setMode(MODES.CREATE);
    dispatch({ type: CLEAR_USITC_HTS_CODES_DROPDOWN_BIZ_CORE_TD });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    try {
      if (open) {
        resetHtsSearch();
        if (update.defaultMode === MODES.CREATE) {
          setMode(MODES.CREATE);
        } else if (update.defaultMode === MODES.EDIT) {
          setMode(MODES.EDIT);
          setFormData({
            HtsCode: update.ref_hts_code_id || null,
            SpiCode: update.spi_code || "",
            PreferentialRate: update.preferential_rate || "",
          });
        } else {
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = () => {
    const obj = {
      ref_hts_code_id: formData.HtsCode,
      spi_code: formData.SpiCode,
      preferential_rate: formData.PreferentialRate,
    };

    if (mode === MODES.CREATE) {
      dispatch(CreateUsitcSpiRateBIZcoreTD(obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else if (mode === MODES.EDIT) {
      dispatch(UpdateUsitcSpiRateBIZcoreTD(update.id, obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else {
      toast.error("Invalid modal");
      handleClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow" width={"100vw"} height={"100vh"}>
        <Box maxWidth={"sm"} className="modal">
          <Box width={"100%"} sx={{ padding: "15px" }}>
            <Typography variant='h6'
              sx={{ color: global["primary"] }} mb={"20px"}>
              {title}
            </Typography>

            {/* USITC HTS Code */}
            <Box mb={"15px"}>
              <Autocomplete
                fullWidth
                options={Array.isArray(htsResults) ? htsResults : []}
                loading={htsSearchLoading}
                disabled={mode === MODES.EDIT}
                value={
                  formData.HtsCode
                  ? (Array.isArray(htsResults) ? htsResults : []).find(h => h.id === formData.HtsCode) || formData.HtsCode
                  : null
                }
                onInputChange={(e, value) => handleHtsSearch(value)}
                onChange={(e, value) => setFormData({ ...formData, HtsCode: value?.id || null })}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                  ? option
                  : (option.hts_code || option.id || "NA")
                }
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === 'string') return option.id === value;
                  return option.id === value?.id;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{option.hts_code || option.id || "NA"}</Typography>
                      {option.description && (
                        <Typography variant="caption" sx={{ color: "grey" }}>
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="USITC HTS code"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {htsSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* SPI Code + Preferential Rate */}
            <Box className="flexCenterSBRow" gap={"15px"} mb={"15px"}>
              <TextField
                label="SPI code"
                fullWidth
                name="SpiCode"
                value={formData.SpiCode}
                onChange={handleChange}
                inputProps={{ maxLength: 5 }}
              />
              <TextField
                label="Preferential rate"
                fullWidth
                name="PreferentialRate"
                value={formData.PreferentialRate}
                onChange={handleChange}
              />
            </Box>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <Button variant='text' onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={!(formData.HtsCode && formData.SpiCode !== "" && formData.PreferentialRate !== "")}
              onClick={handleSubmit}
            >
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default UsitcSpiRatesModal;
